import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CoverageFlatTeamSettingGroupById,
  type CoverageFlatTeamSettingGroupId,
} from "./groups";

type CoverageSettingInput = SettingRegistryMetadataInput;

function setting(metadata: CoverageSettingInput): CoverageSettingInput {
  return metadata;
}

function coverageGroup<
  const Group extends CoverageFlatTeamSettingGroupId,
  const T extends Record<string, CoverageSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const CoverageRegistry = {
  ...coverageGroup("coverage.text_localization", {
    "shopifyExtension/useDefaultCoverageTranslations": setting({
      label: "Use defaults",
      description:
        "Use shared translations of standard Coverage text in the shopper's selected Shopify language. Enabling and saving creates editable language groups. Merchant translations take priority, and custom wording stays unchanged.",
      synonyms: ["default coverage translations", "coverage language defaults"],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "shopify",
          },
        ],
        unavailableBehavior: "hidden",
        reason: "Available for Shopify storefronts.",
      },
    }),
  }),
  ...coverageGroup("coverage.info_modal", {
    "infoModal/sideImage": setting({
      label: "Info modal side image",
      description: "Left-side image shown in the coverage info modal.",
      synonyms: ["modal left image", "modal side image"],
    }),
    "infoModal/logo": setting({
      label: "Info modal logo",
      description: "Logo shown in the coverage info modal header.",
      synonyms: ["modal logo"],
    }),
    "infoModal/headerText": setting({
      label: "Headline text",
      description: "Headline shown at the top of the coverage info modal.",
      synonyms: ["modal headline", "modal title"],
    }),
    "infoModal/subheaderText": setting({
      label: "Body text",
      description:
        "Body copy shown under the headline in the coverage info modal.",
      synonyms: ["modal body text", "modal subheading"],
    }),
    "infoModal/cartIconEnabled": setting({
      label: "Enable for cart toggle",
      description:
        "Show the info icon on the cart coverage toggle that opens this modal.",
      synonyms: ["cart info icon", "cart modal toggle"],
    }),
    "infoModal/checkoutIconEnabled": setting({
      label: "Enable for checkout checkbox",
      description:
        "Show the info icon on the checkout coverage checkbox that opens this modal.",
      synonyms: ["checkout info icon", "checkout modal toggle"],
    }),
    "infoModal/customCss": setting({
      label: "Custom CSS",
      description: "Custom CSS applied to the coverage info modal.",
      synonyms: ["modal custom css", "modal styling"],
    }),
    "infoModal/enableTopLayerModalApi": setting({
      label: "Use top layer API",
      description:
        "Position the info modal with the browser Top Layer API instead of z-index.",
      synonyms: ["top layer modal", "modal positioning"],
    }),
  }),
  ...coverageGroup("coverage.general_coverage", {
    hideCheckbox: setting({
      label: "Show on product page",
      description:
        "Show the Redo return coverage info on the product detail page.",
      synonyms: [
        "show on product page",
        "product page coverage",
        "hide checkbox",
      ],
      valueSemantics: {
        summary:
          "Stored inverted from its label: true hides the coverage info on the product page.",
        trueLabel: "Hidden from the product page",
        falseLabel: "Shown on the product page",
      },
    }),
    "shopifyExtension/coverageExcludePickup": setting({
      label: "Exclude in-store pickup",
      description:
        "Do not add coverage for in-store pickup orders on your Shopify store (checkout checkbox only).",
      synonyms: ["exclude in-store pickup", "in store pickup coverage"],
    }),
    html: setting({
      label: "Info text",
      description:
        "The coverage info text shown next to the coverage toggle on the product page.",
      synonyms: ["info text", "coverage text"],
    }),
    checkboxFontSize: setting({
      label: "Font size",
      description:
        "Font size, in pixels, of the coverage text on the product page.",
      synonyms: ["font size", "coverage font size"],
    }),
    checkboxSelector: setting({
      label: "CSS selector",
      description:
        "CSS selector for placing the Redo coverage info on the product detail page.",
      synonyms: ["css selector", "coverage selector", "placement selector"],
    }),
  }),
  ...coverageGroup("coverage.exclusions", {
    discountCodePartialMatch: setting({
      label: "Require exact match for discount codes",
      description:
        "Require an exact discount-code match (rather than a partial match) when excluding coverage by discount code.",
      synonyms: ["require exact match", "discount code partial match"],
      valueSemantics: {
        summary:
          "Stored inverted from its label: true matches discount codes partially, which is exact-match off.",
        trueLabel: "Partial discount-code matches exclude coverage",
        falseLabel: "Only exact discount-code matches exclude coverage",
      },
    }),
    coverageExcludeTags: setting({
      label: "Exclude order tags",
      description:
        "Coverage will not be added for orders with these Shopify order tags.",
      synonyms: ["exclude order tags", "order tag exclusions"],
    }),
    coverageExcludeSources: setting({
      label: "Exclude sources",
      description:
        "Coverage will not be added for orders from these sources (e.g. POS).",
      synonyms: ["exclude sources", "source exclusions"],
    }),
    coverageExcludeCustomerTags: setting({
      label: "Exclude customer tags",
      description:
        "Coverage will not be offered to customers with these Shopify tags.",
      synonyms: ["exclude customer tags", "customer tag exclusions"],
    }),
    coverageExcludeProperties: setting({
      label: "Exclude properties",
      description:
        "Coverage will not be offered for items with these line-item properties or values.",
      synonyms: ["exclude properties", "property exclusions"],
    }),
    "returns/coverageExcludedVariantMetafields": setting({
      label: "Exclude variant metafields",
      description:
        "Coverage will not be offered for variants matching these metafields.",
      synonyms: ["exclude variant metafields", "metafield exclusions"],
    }),
    coverageExcludeDiscountCodes: setting({
      label: "Exclude product discount codes",
      description:
        "Coverage will not be offered when these discount codes are used.",
      synonyms: ["exclude discount codes", "discount code exclusions"],
    }),
  }),
  ...coverageGroup("coverage.return_coverage", {
    coverage: setting({
      label: "Covered return methods",
      description:
        "Which return methods (store credit, exchange, refund) are covered by return coverage.",
      synonyms: ["covered return methods", "coverage methods"],
    }),
    "returns/finalSaleReturns/enabled": setting({
      label: "Final sale returns coverage",
      description:
        "Allow customers to purchase coverage for final sale items, making them eligible for returns.",
      synonyms: ["final sale returns coverage", "final sale coverage"],
    }),
    "publicapi/discountsAreFinalSales": setting({
      label: "Make sale items ineligible",
      description:
        "Products with a sale (compare-at) price set are ineligible for returns coverage (CommentSold only).",
      synonyms: ["make sale items ineligible", "discounts are final sales"],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
    "publicapi/coverageExcludeLocalPickup": setting({
      label: "Exclude in-store pickup (CommentSold only)",
      description:
        "Do not add coverage for in-store pickup orders on your CommentSold store.",
      synonyms: [
        "commentsold local pickup",
        "commentsold in store pickup",
        "exclude local pickup",
      ],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
    "publicapi/couponedItemsDiscountThreshold": setting({
      label: "Couponed items threshold",
      description:
        "Couponed items discounted above this percentage are treated as final sale and not covered (CommentSold only).",
      synonyms: ["couponed items threshold", "coupon discount threshold"],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
    coverageExcludeProductTags: setting({
      label: "Exclude product tags",
      description:
        "Coverage will not be offered for products with these Shopify tags.",
      synonyms: [
        "exclude product tags from coverage",
        "coverage product tag exclusions",
      ],
    }),
    coverageExcludeCollections: setting({
      label: "Exclude collections",
      description:
        "Coverage will not be offered for products in these collections.",
      synonyms: [
        "exclude collections from coverage",
        "coverage collection exclusions",
      ],
    }),
    "returns/returnsCoverageExcludedSkus": setting({
      label: "Exclude SKUs",
      description: "Coverage will not be offered for products with these SKUs.",
      synonyms: ["exclude skus", "sku exclusions"],
    }),
    coverageExcludeCentEnding: setting({
      label: "Exclude cent endings",
      description:
        "Coverage will not be offered for products whose pre-discount price ends in these cent values.",
      synonyms: ["exclude cent endings", "cent ending exclusions"],
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "comment-sold",
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only rendered for stores with the CommentSold integration connected.",
      },
    }),
  }),
  ...coverageGroup("coverage.credit_incentive", {
    "shopifyExtension/coverageCreditIncentive/enabled": setting({
      label: "Enable coverage credit incentive",
      description:
        "Give store credit to customers who purchase coverage with their order.",
      synonyms: ["coverage credit incentive", "store credit for coverage"],
    }),
    "shopifyExtension/coverageCreditIncentive/rewardType": setting({
      label: "Reward",
      description:
        "Whether shoppers who buy coverage get store credit or a single-use discount code for their next order. Store credit is the default.",
      synonyms: ["coverage reward type", "coverage discount code reward"],
    }),
    "shopifyExtension/coverageCreditIncentive/discountCodePrefix": setting({
      label: "Code prefix",
      description:
        "The start of each coverage reward discount code, 3 to 20 capital letters, numbers or hyphens. Only used when the reward is a discount code.",
      synonyms: ["coverage reward code prefix"],
      availability: {
        conditions: [
          {
            type: "settingEquals",
            settingId: "shopifyExtension/coverageCreditIncentive/rewardType",
            value: "discount_code",
          },
        ],
        unavailableBehavior: "hidden",
        reason: "Only shown when the reward is a discount code.",
      },
    }),
    "shopifyExtension/coverageCreditIncentive/type": setting({
      label: "Incentive type",
      description:
        "Whether the coverage credit is a fixed amount or a percentage of the order.",
      synonyms: ["credit incentive type"],
    }),
    "shopifyExtension/coverageCreditIncentive/fixedAmount": setting({
      label: "Credit amount",
      description:
        "The fixed store credit amount given when the incentive type is a fixed amount.",
      synonyms: ["coverage credit amount"],
    }),
    "shopifyExtension/coverageCreditIncentive/percentageSettings/percentage":
      setting({
        label: "Percentage of order",
        description:
          "The share of the order, excluding coverage, given as store credit when the incentive type is a percentage.",
        synonyms: ["coverage credit percentage"],
      }),
    "shopifyExtension/coverageCreditIncentive/percentageSettings/storeCreditAmountCap":
      setting({
        label: "Maximum credit amount",
        description:
          "The most store credit a single order can earn under a percentage incentive. Leave empty for no limit.",
        synonyms: ["coverage credit cap", "maximum coverage credit"],
      }),
    "shopifyExtension/coverageCreditIncentive/minimumQualifyingPurchaseAmount":
      setting({
        label: "Minimum qualifying purchase amount",
        description:
          "The minimum order amount, excluding the coverage product, needed to earn the credit.",
        synonyms: ["coverage credit minimum purchase"],
      }),
    "shopifyExtension/coverageCreditIncentive/creditExpirationDays": setting({
      label: "Coverage credit expiration days",
      description:
        "Days until the coverage store credit expires. Leave empty for no expiration.",
      synonyms: ["coverage credit expiration"],
    }),
    "shopifyExtension/coverageCreditIncentive/sendEmail": setting({
      label: "Send email notification",
      description:
        "Email customers when they receive store credit for buying coverage.",
      synonyms: ["coverage credit email"],
    }),
    "shopifyExtension/coverageCreditIncentive/sendExpirationReminderEmail":
      setting({
        label: "Send expiration reminder email",
        description:
          "Email customers before their coverage store credit expires, if they still have credit.",
        synonyms: ["coverage credit expiring reminder"],
      }),
    "shopifyExtension/coverageCreditIncentive/expirationReminderDaysBefore":
      setting({
        label: "Days before expiration",
        description:
          "How many days before the credit expires to send the reminder email.",
        synonyms: ["coverage credit reminder days"],
      }),
  }),
  ...coverageGroup("coverage.checkout_ui_customizations", {
    "shopifyExtension/checkoutUICustomizations/titleTextSize": setting({
      label: "Title text size",
      description:
        "Text size of the coverage element title in Shopify checkout.",
      synonyms: ["title text size", "checkout title size"],
    }),
    "shopifyExtension/checkoutUICustomizations/subtextSize": setting({
      label: "Subtext size",
      description:
        "Text size of the coverage element subtext in Shopify checkout.",
      synonyms: ["subtext size", "checkout subtext size"],
    }),
    "shopifyExtension/checkoutUICustomizations/spacing": setting({
      label: "Spacing above/below element",
      description:
        "Spacing and dividers above and below the coverage element in Shopify checkout.",
      synonyms: ["checkout spacing", "element spacing"],
    }),
  }),
  ...coverageGroup("coverage.pre_protected_orders", {
    preProtectedOrdersEnabled: setting({
      label: "Pre-protected orders",
      description:
        "Automatically add return coverage to eligible orders placed without it.",
      synonyms: ["pre-protected orders", "retroactive coverage"],
    }),
  }),
  ...coverageGroup("coverage.advanced_coverage", {
    "shopifyExtension/variantNaming": setting({
      label: "Variant naming",
      description:
        "Examples: alphanumeric: U6E6Z, custom: custom-1, price-based: redo-2.98.",
      synonyms: ["coverage variant naming", "Shopify coverage variant names"],
    }),
    "shopifyExtension/deleteCartProperties": setting({
      label: "Delete Redo cart properties from orders",
      description:
        "Remove the cart properties Redo adds during checkout from each Shopify order after it is created.",
      synonyms: ["delete cart properties", "cart properties"],
    }),
  }),
  ...coverageGroup("coverage.digital_warehouse", {
    "shopifyExtension/digitalWarehouse/enabled": setting({
      label: "Fulfill Redo from the Redo digital warehouse",
      description:
        "Keeps the Redo coverage product in stock through its own warehouse location in Shopify instead of your locations. Turning it on is checked against your Shopify store first.",
      synonyms: ["redo digital warehouse", "redo product warehouse location"],
    }),
  }),
};

type CoverageFlatTeamSettingKey = keyof typeof CoverageRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const CoverageFlatTeamSettingKeys = Object.keys(
  CoverageRegistry,
) as CoverageFlatTeamSettingKey[];

export const CoverageFlatTeamSettingCatalogDefinitions =
  CoverageFlatTeamSettingKeys.map((key) => {
    const definition = CoverageRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: CoverageFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CoverageFlatTeamSettingKey,
    CoverageFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CoverageFlatTeamSettingKey>
  >[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const CoverageFlatTeamSettingMetadataByKey = Object.fromEntries(
  CoverageFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<CoverageFlatTeamSettingKey, SettingMetadata>;
